<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { CheckIcon, XIcon } from '@heroicons/vue/outline';
import useUpdateEntityLocationMutation from '@/api/mutations/EntityLocation/updateEntityLocation.mutation';
import AtIconButton from '@/components/atoms/AtIconButton.vue';
import AtInput from '@/components/atoms/AtInput/AtInput.vue';

type Props = {
  projectId: string;
  projectName: string;
  existingProjectsNames: string[];
};

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const { mutate } = useUpdateEntityLocationMutation({
  update: (store) => {
    // Refetch projects list after project name is updated.
    store.evict({
      id: 'ROOT_QUERY',
      fieldName: 'entityLocationSummaries',
    });
  },
});

const form = reactive({
  name: '',
});
watch(
  () => props.projectName,
  (newProjectName) => {
    form.name = newProjectName;
  },
  { immediate: true },
);

const validationRules = computed(() => {
  const existingNames = new Set(props.existingProjectsNames);

  return {
    name: {
      required: helpers.withMessage(t('Project name is required.'), required),
      unique: helpers.withMessage(
        t('A project with this name already exists.'),
        (name: string) => !existingNames.has(name),
      ),
    },
  };
});
const v$ = useVuelidate(validationRules, form);

const formRef = ref<HTMLElement>();
onClickOutside(formRef, handleClose);

function handleSubmit() {
  mutate({
    updateEntityLocationInput: {
      _id: props.projectId,
      name: form.name,
    },
  });
  handleClose();
}

function handleClose() {
  emit('close');
}
</script>

<template>
  <form
    ref="formRef"
    data-cy="projectRenameForm"
    class="flex space-x-1"
    @submit.prevent="handleSubmit"
  >
    <AtInput
      v-model="form.name"
      class="!py-1 !px-2"
      :errors="v$.name.$errors"
      @blur="v$.name.$touch"
    />
    <div class="flex-1 flex space-x-1 items-center h-[30px]">
      <AtIconButton
        type="submit"
        class="text-blue-600 !w-4 !h-4 !px-0"
        :icon="CheckIcon"
        :title="t('Save')"
      />
      <AtIconButton
        type="button"
        class="text-rose-600 hover:text-rose-600 !w-4 !h-4 !px-0"
        :icon="XIcon"
        :title="t('Cancel')"
        @click="handleClose"
      />
    </div>
  </form>
</template>
